import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form, Button, Spinner, Badge, Nav, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
    useChannelConfig,
    ChannelConfigData,
    VicidialCampaignOption,
    fetchVicidialCampaignOptions,
} from './hooks/useChannelConfig'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { GET_ALL_CHANNEL_CONFIGS } from '../../gql/queries/channelConfigAdminQuery'
import { useAuth } from '../../../modules/auth'
import { STORAGE_NAMES } from '../../helpers/consts'
import { useOrgChannels } from './hooks/useOrgChannels'
import { useChannelValidation } from './hooks/useChannelValidation'
import { ChannelFormModal } from '../assignmentGroups/components/ChannelFormModal'
import { Channel } from '../../types/assignmentGroups'
import { DifySection } from './components/DifySection'
import { PromptConfigSection } from './components/PromptConfigSection'
import { ChannelMessagesModal } from './components/ChannelMessagesModal'
import { UPDATE_DIFY_CONFIG, UPDATE_PROMPT_CONFIG } from '../../gql/mutations/channelMutations'
import { URL as BACKEND_URL } from '../../gql/client'

type ChannelKind = 'SMS' | 'WHATSAPP' | 'EMAIL'

interface ChannelRow {
    ownerId: string
    ownerType: string
    ownerLabel: string
    channelKind: ChannelKind
    displayNumber: string
    sidMasked?: string | null
    webhookPath?: string | null
    issues: string[]
    isFirstOfOwner: boolean
    ownerChannelCount: number
}

const CHANNEL_KIND_LABEL: Record<ChannelKind, string> = {
    SMS: 'SMS',
    WHATSAPP: 'WhatsApp',
    EMAIL: 'Email',
}

const SECTION_ORDER: { ownerType: string; title: string }[] = [
    { ownerType: 'organization', title: 'Organización' },
    { ownerType: 'group', title: 'Assignment Group' },
    { ownerType: 'user', title: 'Usuarios' },
]

const buildChannelRows = (allChannelConfigs: any[]): ChannelRow[] => {
    const rows: ChannelRow[] = []

    allChannelConfigs.forEach((owner: any) => {
        const ownerChannels: Omit<ChannelRow, 'isFirstOfOwner' | 'ownerChannelCount'>[] = []

        if (owner.twilioEnabled && owner.twilioPhoneNumber) {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'SMS',
                displayNumber: owner.twilioPhoneNumber,
                sidMasked: owner.twilioAccountSidMasked,
                webhookPath: (owner.webhookPaths || []).find((p: string) => p.includes('/sms/')) || null,
                issues: owner.issues || [],
            })
        }

        if (owner.whatsappProvider && owner.whatsappProvider !== 'NONE') {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'WHATSAPP',
                displayNumber: owner.whatsappNumber || '—',
                sidMasked: owner.whatsappAccountSidMasked,
                webhookPath: (owner.webhookPaths || []).find((p: string) => p.includes('/whatsapp/')) || null,
                issues: owner.issues || [],
            })
        }

        if (owner.emailEnabled && owner.emailFrom) {
            ownerChannels.push({
                ownerId: owner.ownerId,
                ownerType: owner.ownerType,
                ownerLabel: owner.ownerLabel,
                channelKind: 'EMAIL',
                displayNumber: owner.emailFrom,
                sidMasked: null,
                webhookPath: null,
                issues: owner.issues || [],
            })
        }

        ownerChannels.forEach((row, idx) => {
            rows.push({
                ...row,
                isFirstOfOwner: idx === 0,
                ownerChannelCount: ownerChannels.length,
            })
        })
    })

    return rows
}

const OWNER_BADGE: Record<string, { label: string; variant: string; textClass: string }> = {
    organization: { label: 'Organización', variant: 'primary', textClass: 'text-white' },
    group: { label: 'Grupo', variant: 'info', textClass: 'text-white' },
    // Metronic's "secondary" is a light gray ($gray-300), so it needs dark text for contrast.
    user: { label: 'Usuario', variant: 'secondary', textClass: 'text-dark' },
}

const ISSUE_LABELS: Record<string, string> = {
    duplicate_number: 'Este número está configurado en más de un canal',
    sid_mismatch: 'El mismo número aparece con distinto Account SID en distintas configs',
}

export const ChannelConfigPage = () => {
    const { auth } = useAuth()
    const navigate = useNavigate()
    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const currentUserOrgId = useGetUser((s) => (s.user?.user as any)?.organization?.id)
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

    const [activeTab, setActiveTab] = useState<'settings' | 'allChannels'>('settings')

    // Organization selector state
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined)
    const { data: orgData, loading: loadingOrgs } = useQuery(GET_ALL_ORGANIZATIONS, {
        variables: { first: 100 },
        skip: !isSuperAdmin,
    })
    const organizations = useMemo(
        () => orgData?.allOrganizations?.edges?.map((e: any) => e.node) || [],
        [orgData]
    )

    // Set initial org once loaded
    useEffect(() => {
        if (isSuperAdmin && organizations.length > 0 && !selectedOrgId) {
            const matchedOrg = currentUserOrgId
                ? organizations.find((o: any) => parseId(o.id) === parseId(currentUserOrgId))
                : undefined
            const defaultOrg = matchedOrg?.id || organizations[0]?.id
            setSelectedOrgId(defaultOrg)
        }
    }, [isSuperAdmin, organizations, selectedOrgId, currentUserOrgId])

    // Decode relay ID to numeric ID for the REST API
    const decodedOrgId = selectedOrgId ? String(parseId(selectedOrgId)) : undefined
    const { config, loading, saving, saveOrgConfig } = useChannelConfig(
        isSuperAdmin ? decodedOrgId : undefined
    )
    const { saving: channelsSaving, saveChannel } = useOrgChannels(decodedOrgId)
    const [updateDifyConfigGql] = useMutation(UPDATE_DIFY_CONFIG)
    const [updatePromptConfigGql] = useMutation(UPDATE_PROMPT_CONFIG)

    const { data: allChannelConfigsData, loading: loadingAllChannelConfigs } = useQuery(GET_ALL_CHANNEL_CONFIGS, {
        variables: { organizationId: isSuperAdmin ? decodedOrgId : undefined },
        skip: activeTab !== 'allChannels',
    })
    const allChannelConfigs = allChannelConfigsData?.allChannelConfigs || []
    const channelRows = useMemo(() => buildChannelRows(allChannelConfigs), [allChannelConfigs])
    const channelRowsBySection = useMemo(() => {
        const bySection: Record<string, ChannelRow[]> = { organization: [], group: [], user: [] }
        channelRows.forEach((row) => {
            if (bySection[row.ownerType]) {
                bySection[row.ownerType].push(row)
            }
        })
        return bySection
    }, [channelRows])

    const [messagesModalRow, setMessagesModalRow] = useState<ChannelRow | null>(null)

    const [form, setForm] = useState<Partial<ChannelConfigData>>({})
    const [isDirty, setIsDirty] = useState(false)
    const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
    const [loadingCampaigns, setLoadingCampaigns] = useState(false)

    // Channel modal state
    const [channelModalOpen, setChannelModalOpen] = useState(false)
    const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

    // Validation hooks
    const {
        validateTwilioSms,
        validateTwilioWhatsapp,
        validateBotmakerWhatsapp,
        validateMetaWhatsapp,
    } = useChannelValidation()

    // Validation state
    const [validatingTwilioSms, setValidatingTwilioSms] = useState(false)
    const [validatingWhatsapp, setValidatingWhatsapp] = useState(false)

    // Sync form when config loads or org changes
    useEffect(() => {
        if (!loading) {
            setForm(config)
            setIsDirty(false)
            setCampaignOptions([])
        }
    }, [config, loading])

    const getToken = useCallback(() => {
        return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
    }, [auth?.api_token])

    const visibleCampaignOptions = useMemo(() => {
        const currentCampaignId = String(form.vicidial_campaign_id || '').trim()
        if (!currentCampaignId || campaignOptions.some((campaign) => campaign.campaign_id === currentCampaignId)) {
            return campaignOptions
        }

        return [
            {
                campaign_id: currentCampaignId,
                campaign_name: 'Current organization default',
                active: 'Y',
            },
            ...campaignOptions,
        ]
    }, [campaignOptions, form.vicidial_campaign_id])

    const handleChange = (field: keyof ChannelConfigData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setIsDirty(true)
    }

    const handleOrgChange = (orgId: string) => {
        setSelectedOrgId(orgId)
        setIsDirty(false)
        setCampaignOptions([])
    }

    const handleCopyWebhookPath = (path: string) => {
        navigator.clipboard.writeText(`${BACKEND_URL}${path}`)
        toast.success('Webhook URL copied')
    }

    const handleEditChannelOwner = (row: any) => {
        if (row.ownerType === 'user') {
            navigate(`/user/${row.ownerId}`)
        } else if (row.ownerType === 'group') {
            navigate('/assignment-groups')
        } else {
            navigate('/organizations')
        }
    }

    const handleLoadCampaigns = async () => {
        const token = getToken()
        if (!token) {
            toast.error('You must be logged in to load PolicyDial campaigns.')
            return
        }

        const baseUrl = String(form.vicidial_base_url || '').trim()
        const apiUser = String(form.vicidial_api_user || '').trim()
        const apiPassword = String(form.vicidial_api_password || '').trim()

        if (!baseUrl || !apiUser || !apiPassword) {
            toast.error('Base URL, API User and API Password are required to load PolicyDial campaigns.')
            return
        }

        setLoadingCampaigns(true)
        try {
            const campaigns = await fetchVicidialCampaignOptions({
                token,
                baseUrl,
                apiUser,
                apiPassword,
            })
            setCampaignOptions(campaigns)

            if (!campaigns.length) {
                toast.info('No PolicyDial campaigns were returned for this organization.')
                return
            }

            toast.success('PolicyDial campaigns loaded successfully.')
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Unable to load PolicyDial campaigns.'
            toast.error(message)
            setCampaignOptions([])
        } finally {
            setLoadingCampaigns(false)
        }
    }

    const handleSave = async () => {
        // Save Dify config via GraphQL mutation
        try {
            await updateDifyConfigGql({
                variables: {
                    scope: 'organization',
                    targetId: decodedOrgId ? parseInt(decodedOrgId, 10) : undefined,
                    difyEnabled: Boolean(form.dify_enabled),
                    difyApiKey: form.dify_api_key || '',
                    difyApiUrl: form.dify_api_url || 'https://api.dify.ai/v1',
                    difyAppId: form.dify_app_id || '',
                    aiProvider: form.ai_provider || 'DIFY',
                    llmProvider: form.llm_provider || 'ANTHROPIC',
                    llmApiKey: form.llm_api_key || '',
                    llmModel: form.llm_model || '',
                },
            })
        } catch (gqlErr) {
            console.error('Error saving Dify via GraphQL:', gqlErr)
        }

        try {
            await updatePromptConfigGql({
                variables: {
                    scope: 'organization',
                    targetId: decodedOrgId ? parseInt(decodedOrgId, 10) : undefined,
                    promptHeader: form.prompt_header || '',
                    promptBody: form.prompt_body || '',
                    promptFooter: form.prompt_footer || '',
                    shareContactInfo: form.share_contact_info,
                    shareQuotes: form.share_quotes,
                    sharePolicies: form.share_policies,
                    shareLeads: form.share_leads,
                    shareConsentStatus: form.share_consent_status,
                },
            })
        } catch (gqlErr) {
            console.error('Error saving Prompt config via GraphQL:', gqlErr)
        }

        const result = await saveOrgConfig(form)
        if (result.ok) {
            toast.success('Channel configuration saved successfully')
            setIsDirty(false)
        } else {
            toast.error(`Error saving channel configuration: ${result.error}`, { autoClose: 8000 })
        }
    }

    const handleSaveChannel = async (data: Partial<Channel>) => {
        try {
            const saved = await saveChannel(data)
            if (saved) {
                toast.success(data.id ? 'Channel updated' : 'Channel created')
                setChannelModalOpen(false)
                setEditingChannel(null)
            }
            return saved as any
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error || 'Error saving channel'
            toast.error(errorMessage)
            throw error
        }
    }

    const handleValidateTwilioSms = async () => {
        const accountSid = form.twilio_account_sid?.trim()
        const authToken = form.twilio_auth_token?.trim()

        if (!accountSid || !authToken) {
            toast.warning('Please fill in Account SID and Auth Token')
            return
        }

        setValidatingTwilioSms(true)
        try {
            const result = await validateTwilioSms({
                variables: { accountSid, authToken },
            })
            if (result.data?.validateTwilioSms?.payload?.valid) {
                toast.success(result.data.validateTwilioSms.payload.message)
            } else {
                toast.error(result.data?.validateTwilioSms?.payload?.message || 'Validation failed')
            }
        } catch (error: any) {
            toast.error(error.message || 'Validation error')
        } finally {
            setValidatingTwilioSms(false)
        }
    }

    const handleValidateWhatsapp = async () => {
        const provider = form.whatsapp_provider?.trim()

        if (!provider || provider === 'NONE') {
            toast.warning('Please select a WhatsApp provider')
            return
        }

        setValidatingWhatsapp(true)
        try {
            let result
            if (provider === 'TWILIO') {
                const accountSid = form.twilio_whatsapp_account_sid?.trim() || form.twilio_account_sid?.trim()
                const authToken = form.twilio_whatsapp_auth_token?.trim() || form.twilio_auth_token?.trim()

                if (!accountSid || !authToken) {
                    toast.warning('Please fill in Twilio Account SID and Auth Token')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateTwilioWhatsapp({
                    variables: { accountSid, authToken },
                })
            } else if (provider === 'BOTMAKER') {
                const apiToken = form.botmaker_api_token?.trim()
                const defaultChannelId = form.botmaker_default_channel_id?.trim()
                const apiUrl = form.botmaker_api_url?.trim() || undefined

                if (!apiToken || !defaultChannelId) {
                    toast.warning('Please fill in API Token and Default Channel ID')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateBotmakerWhatsapp({
                    variables: { apiToken, defaultChannelId, apiUrl },
                })
            } else if (provider === 'META') {
                const accessToken = form.meta_whatsapp_access_token?.trim()
                const phoneNumberId = form.meta_whatsapp_phone_number_id?.trim()

                if (!accessToken || !phoneNumberId) {
                    toast.warning('Please fill in Access Token and Phone Number ID')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateMetaWhatsapp({
                    variables: { accessToken, phoneNumberId },
                })
            }

            if (result?.data) {
                const payload = (Object.values(result.data)[0] as any)?.payload
                if (payload?.valid) {
                    toast.success(payload.message)
                } else {
                    toast.error(payload?.message || 'Validation failed')
                }
            }
        } catch (error: any) {
            toast.error(error.message || 'Validation error')
        } finally {
            setValidatingWhatsapp(false)
        }
    }

    if (loading || (isSuperAdmin && loadingOrgs)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="container-xxl py-5">
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
                    <div>
                        <h4 className="alert-heading">Permission Denied</h4>
                        <p className="mb-0">You do not have permission to configure organization channels. Please contact your administrator.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-xxl py-5">
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fs-2 fw-bold text-gray-900 mb-1">Channel Configuration</h1>
                    <p className="text-muted fs-6 mb-0">
                        Configure communication channels for your organization
                    </p>
                </div>
                {activeTab === 'settings' && (
                    <div className="d-flex align-items-center gap-3">
                        {config.webhook_token && (
                            <span className="text-muted fs-7">
                                Webhook: <code className="text-primary">{config.webhook_token.slice(0, 12)}...</code>
                            </span>
                        )}
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                            className="px-6"
                        >
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-1"></i>
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {isAdmin && (
                <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'settings' | 'allChannels')} className="mb-5">
                    <Nav.Item>
                        <Nav.Link eventKey="settings">
                            <i className="bi bi-gear me-2" />Settings
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="allChannels">
                            <i className="bi bi-broadcast-pin me-2" />Channels de la Organización
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            )}

            {activeTab === 'allChannels' ? (
                loadingAllChannelConfigs ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        {SECTION_ORDER.map((section) => {
                            const rows = channelRowsBySection[section.ownerType] || []
                            const ownerBadge = OWNER_BADGE[section.ownerType]
                            return (
                                <Card className="shadow-sm mb-4" key={section.ownerType}>
                                    <Card.Header className="border-0 pt-5">
                                        <h4 className="card-title fw-bold">
                                            <Badge bg={ownerBadge?.variant || 'secondary'} className={`me-2 ${ownerBadge?.textClass || 'text-dark'}`}>
                                                {ownerBadge?.label || section.title}
                                            </Badge>
                                            {section.title}
                                        </h4>
                                    </Card.Header>
                                    <Card.Body className="py-3">
                                        <div className="table-responsive">
                                            <Table hover className="align-middle">
                                                <thead>
                                                    <tr className="text-muted fw-bold fs-7 text-uppercase">
                                                        <th>Información de Usuario</th>
                                                        <th>Channel</th>
                                                        <th>Webhook</th>
                                                        <th>Info</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map((row) => (
                                                        <tr key={`${row.ownerId}-${row.channelKind}`}>
                                                            {row.isFirstOfOwner && (
                                                                <td rowSpan={row.ownerChannelCount}>
                                                                    {row.ownerLabel}
                                                                </td>
                                                            )}
                                                            <td>
                                                                <Badge bg="light" text="dark" className="me-1">
                                                                    {CHANNEL_KIND_LABEL[row.channelKind]}
                                                                </Badge>
                                                                <div>{row.displayNumber}</div>
                                                                {row.sidMasked && (
                                                                    <div className="text-muted fs-8">SID {row.sidMasked}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {row.webhookPath ? (
                                                                    <div className="d-flex align-items-center gap-1">
                                                                        <code className="fs-8 text-break">{row.webhookPath}</code>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-icon btn-light p-1"
                                                                            style={{ minWidth: '22px', height: '22px' }}
                                                                            title="Copiar URL completa"
                                                                            onClick={() => handleCopyWebhookPath(row.webhookPath as string)}
                                                                        >
                                                                            <i className="bi bi-clipboard fs-8" />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {row.issues?.length > 0 && (
                                                                    <OverlayTrigger
                                                                        overlay={
                                                                            <Tooltip>
                                                                                {row.issues.map((issue: string) => ISSUE_LABELS[issue] || issue).join(' · ')}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <Badge bg="warning" text="dark">
                                                                            <i className="bi bi-exclamation-triangle-fill me-1" />
                                                                            Revisar
                                                                        </Badge>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-light-primary"
                                                                        onClick={() => handleEditChannelOwner(row)}
                                                                    >
                                                                        Editar
                                                                    </button>
                                                                    <OverlayTrigger
                                                                        overlay={
                                                                            row.channelKind === 'EMAIL' ? (
                                                                                <Tooltip>El historial de mensajes aún no está disponible para Email.</Tooltip>
                                                                            ) : (
                                                                                <Tooltip>Ver historial de mensajes de este canal.</Tooltip>
                                                                            )
                                                                        }
                                                                    >
                                                                        <span>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-light-info"
                                                                                disabled={row.channelKind === 'EMAIL'}
                                                                                onClick={() => setMessagesModalRow(row)}
                                                                            >
                                                                                Ver mensajes
                                                                            </button>
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {rows.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5} className="text-center text-muted py-6">
                                                                No hay channels configurados en esta sección.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )
                        })}
                        <ChannelMessagesModal
                            isOpen={!!messagesModalRow}
                            onClose={() => setMessagesModalRow(null)}
                            phoneNumber={messagesModalRow?.displayNumber}
                            channelKind={messagesModalRow?.channelKind}
                            ownerLabel={messagesModalRow?.ownerLabel}
                        />
                    </>
                )
            ) : (
            <>
            {/* Organization Selector — SuperAdmin only */}
            {isSuperAdmin && (
                <Card className="mb-5 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)' }}>
                    <Card.Body className="py-4">
                        <div className="d-flex align-items-center gap-4">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-building fs-3 text-primary"></i>
                                <span className="fw-bold text-gray-800 fs-5">Organization</span>
                            </div>
                            <Form.Select
                                value={selectedOrgId || ''}
                                onChange={(e) => handleOrgChange(e.target.value)}
                                className="mw-400px"
                                style={{ borderColor: '#c5d2e8' }}
                            >
                                <option value="" disabled>Select organization...</option>
                                {organizations.map((org: any) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {selectedOrgId && (
                                <Badge bg="light" text="dark" className="fs-7 px-3 py-2 border">
                                    Configuring: <strong>{organizations.find((o: any) => parseId(o.id) === parseId(selectedOrgId))?.name || config.organization_name}</strong>
                                </Badge>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* ═════ TELEPHONY SYSTEM ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-telephone-forward fs-2 text-primary"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Telephony System</h4>
                    </div>

                    {/* ─── Provider Toggle ─── */}
                    <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded bg-light border">
                        <div className="fw-semibold text-gray-700 flex-shrink-0">☎️ RingCentral</div>
                        <Form.Check
                            type="switch"
                            id="provider_toggle"
                            label={
                                <span className="d-inline-flex align-items-center gap-1">
                                    <img
                                        src="/media/tfi/policydial-icon.svg"
                                        alt="PolicyDial"
                                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                                    />
                                    PolicyDial
                                </span>
                            }
                            checked={form.telephony_provider === 'VICIDIAL'}
                            onChange={(e) => handleChange('telephony_provider', e.target.checked ? 'VICIDIAL' : 'RINGCENTRAL')}
                            className="fw-semibold"
                        />
                    </div>

                    {/* ─── PolicyDial Configuration ─── */}
                    {form.telephony_provider === 'VICIDIAL' && (
                        <div className="border rounded p-4 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                <img
                                    src="/media/tfi/policydial-webphone.svg"
                                    alt="PolicyDial"
                                    style={{ height: '32px', objectFit: 'contain' }}
                                />
                                <h5 className="fw-bold text-gray-800 mb-0">Configuration</h5>
                                <Badge bg={form.vicidial_enabled ? 'success' : 'secondary'} className="ms-auto">
                                    {form.vicidial_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-12">
                                    <Form.Check
                                        type="switch"
                                        id="vicidial_enabled"
                                        label="Enable PolicyDial telephony"
                                        checked={form.vicidial_enabled || false}
                                        onChange={(e) => handleChange('vicidial_enabled', e.target.checked)}
                                        className="mb-3"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Base URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://vici.policytools.com"
                                            value={form.vicidial_base_url || ''}
                                            onChange={(e) => handleChange('vicidial_base_url', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Webphone URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://phone.viciphone.com/v3.0/viciphone.php"
                                            value={form.vicidial_webphone_url || ''}
                                            onChange={(e) => handleChange('vicidial_webphone_url', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            Used as fallback when the AGENT API cannot return the active session URL.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API User</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="6666"
                                            value={form.vicidial_api_user || ''}
                                            onChange={(e) => handleChange('vicidial_api_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API Password</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="off"
                                            placeholder="Enter API password"
                                            value={form.vicidial_api_password || ''}
                                            onChange={(e) => handleChange('vicidial_api_password', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Default agent user</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="support01"
                                            value={form.vicidial_agent_user || ''}
                                            onChange={(e) => handleChange('vicidial_agent_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Default phone code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="1"
                                            value={form.vicidial_phone_code || ''}
                                            onChange={(e) => handleChange('vicidial_phone_code', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Campaign</Form.Label>
                                        {visibleCampaignOptions.length > 0 ? (
                                            <Form.Select
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            >
                                                <option value=''>Select organization default campaign...</option>
                                                {visibleCampaignOptions.map((campaign) => (
                                                    <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                                        {campaign.campaign_id} - {campaign.campaign_name}
                                                        {campaign.active === 'Y' ? '' : ' (Inactive)'}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                placeholder="0001"
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            />
                                        )}
                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                            <Button
                                                variant="light-primary"
                                                size="sm"
                                                onClick={handleLoadCampaigns}
                                                disabled={loadingCampaigns}
                                            >
                                                {loadingCampaigns ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Loading...
                                                    </>
                                                ) : visibleCampaignOptions.length > 0 ? (
                                                    'Refresh campaigns'
                                                ) : (
                                                    'Load campaigns'
                                                )}
                                            </Button>
                                            <Form.Text className="text-muted mb-0">
                                                Super admins can set the organization default here. Users can still choose their active PolicyDial campaign from <code>Integrations</code>.
                                            </Form.Text>
                                        </div>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Auto dial delay (seconds)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            step={1}
                                            placeholder="5"
                                            value={form.vicidial_dial_queue_delay_seconds ?? 5}
                                            onChange={(e) =>
                                                handleChange(
                                                    'vicidial_dial_queue_delay_seconds',
                                                    e.target.value === '' ? 5 : Math.max(0, Number(e.target.value))
                                                )
                                            }
                                        />
                                        <Form.Text className="text-muted">
                                            Wait time between saving the disposition and placing the next PolicyDial queue call.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* ═════ MESSAGING & CHANNELS ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-chat-left-text fs-2 text-info"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Messaging & Channels</h4>
                    </div>

                    {/* ─── Twilio SMS ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-chat-dots fs-4 text-info"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">Twilio SMS</h5>
                            <Form.Check
                                type="switch"
                                id="twilio_enabled"
                                checked={form.twilio_enabled || false}
                                onChange={(e) => handleChange('twilio_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.twilio_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.twilio_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.twilio_enabled && (
                            <>
                                <div className="row g-1 mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Account SID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="AC..."
                                                value={form.twilio_account_sid || ''}
                                                onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Auth Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Token"
                                                value={form.twilio_auth_token || ''}
                                                onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="+1858..."
                                                value={form.twilio_phone_number || ''}
                                                onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex justify-content-center">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateTwilioSms}
                                            disabled={validatingTwilioSms}
                                            className="w-100"
                                        >
                                            {validatingTwilioSms ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <i className="bi bi-beaker"></i>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                <div className="row g-1 mt-3 align-items-end">
                                    <div className="col-md-8">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">
                                                Elastic SIP Trunk SID <span className="text-muted fw-normal">· Twilio Voice</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="TK..."
                                                value={form.twilio_elastic_trunk_sid || ''}
                                                onChange={(e) => handleChange('twilio_elastic_trunk_sid', e.target.value)}
                                                size="sm"
                                            />
                                            <Form.Text className="text-muted fs-9">
                                                Trunk que engancha los números comprados (Caller ID / Replace) y enruta a VICIdial. Vacío = usa el valor del entorno.
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Inbound:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`${BACKEND_URL}/webhooks/twilio/sms/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/sms/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-warning border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Status:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`${BACKEND_URL}/webhooks/twilio/status/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/status/`)
                                                        toast.success('Status callback URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy status callback URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {/* ─── WhatsApp Integration (Multi-Provider) ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-whatsapp fs-4 text-success"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">WhatsApp</h5>
                            <Form.Select
                                value={form.whatsapp_provider || 'NONE'}
                                onChange={(e) => handleChange('whatsapp_provider', e.target.value)}
                                className="mw-250px ms-2"
                                size="sm"
                            >
                                <option value="NONE">Disabled</option>
                                <option value="BOTMAKER">Botmaker</option>
                                <option value="TWILIO">Twilio</option>
                                <option value="META">Meta Cloud API</option>
                            </Form.Select>
                            <Badge bg={form.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className="ms-2">
                                {form.whatsapp_provider !== 'NONE' ? form.whatsapp_provider : 'Disabled'}
                            </Badge>
                        </div>

                        {/* Botmaker WhatsApp Config */}
                        {form.whatsapp_provider === 'BOTMAKER' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">API Token</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                autoComplete="off"
                                                rows={1}
                                                placeholder="JWT"
                                                value={form.botmaker_api_token || ''}
                                                onChange={(e) => handleChange('botmaker_api_token', e.target.value)}
                                                style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Channel ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="ID"
                                                value={form.botmaker_default_channel_id || ''}
                                                onChange={(e) => handleChange('botmaker_default_channel_id', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone Line</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="18586096799"
                                                value={form.botmaker_phone_line_number || ''}
                                                onChange={(e) => handleChange('botmaker_phone_line_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <div className="row g-1 mt-2 align-items-center">
                                        <div className="col-auto">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 mb-0">Webhook:</Form.Label>
                                        </div>
                                        <div className="col-auto flex-grow-1">
                                            <code className="text-break text-muted fs-8">{`https://app.policytools.com/webhooks/botmaker/${config.webhook_token}/`}</code>
                                        </div>
                                        <div className="col-auto">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`https://app.policytools.com/webhooks/botmaker/${config.webhook_token}/`)
                                                    toast.success('Webhook URL copied')
                                                }}
                                                className="p-1"
                                                style={{ minWidth: '28px' }}
                                            >
                                                <i className="bi bi-files" style={{ fontSize: '0.75rem' }}></i>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Twilio WhatsApp Config */}
                        {form.whatsapp_provider === 'TWILIO' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Account SID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="AC..."
                                                value={form.twilio_whatsapp_account_sid || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_account_sid', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Auth Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Token"
                                                value={form.twilio_whatsapp_auth_token || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_auth_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">WhatsApp #</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="+1858"
                                                value={form.twilio_whatsapp_number || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Inbound:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`${BACKEND_URL}/webhooks/twilio/whatsapp/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/whatsapp/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-warning border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Status:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`${BACKEND_URL}/webhooks/twilio/status/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/twilio/status/`)
                                                        toast.success('Status callback URL copied')
                                                     }}
                                                    className="p-1"
                                                    title="Copy status callback URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Meta WhatsApp Config */}
                        {form.whatsapp_provider === 'META' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Access Token</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="EAAL..."
                                                value={form.meta_whatsapp_access_token || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_access_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="ID"
                                                value={form.meta_whatsapp_phone_number_id || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_phone_number_id', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Verify Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Token"
                                                value={form.meta_whatsapp_verify_token || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_verify_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <div className="border-top mt-2 pt-2">
                                        <div className="row g-2 align-items-center mb-2 p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Webhook:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`${BACKEND_URL}/webhooks/meta/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`${BACKEND_URL}/webhooks/meta/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Verify Token:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-dark fs-7 d-block">{form.meta_whatsapp_verify_token || '—'}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(form.meta_whatsapp_verify_token || '')
                                                        toast.success('Verify token copied')
                                                    }}
                                                    disabled={!form.meta_whatsapp_verify_token}
                                                    className="p-1"
                                                    title="Copy verify token"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* ─── Dify Integration ─── */}
                    <div className="pt-4 border-top mt-4">
                        <DifySection
                            difyEnabled={form.dify_enabled || false}
                            difyApiKey={form.dify_api_key || ''}
                            difyApiUrl={form.dify_api_url || 'https://api.dify.ai/v1'}
                            difyAppId={form.dify_app_id || ''}
                            onDifyEnabledChange={(value) => handleChange('dify_enabled', value)}
                            onDifyApiKeyChange={(value) => handleChange('dify_api_key', value)}
                            onDifyApiUrlChange={(value) => handleChange('dify_api_url', value)}
                            onDifyAppIdChange={(value) => handleChange('dify_app_id', value)}
                            organizationId={decodedOrgId || ''}
                            aiProvider={form.ai_provider || 'DIFY'}
                            onAiProviderChange={(value) => handleChange('ai_provider', value)}
                            llmProvider={form.llm_provider || 'ANTHROPIC'}
                            onLlmProviderChange={(value) => handleChange('llm_provider', value)}
                            llmApiKey={form.llm_api_key || ''}
                            onLlmApiKeyChange={(value) => handleChange('llm_api_key', value)}
                            llmModel={form.llm_model || ''}
                            onLlmModelChange={(value) => handleChange('llm_model', value)}
                        />

                        {/* ─── Prompt de IA Conversacional (solo si IA Conversacional está activa) ─── */}
                        {form.dify_enabled && (
                            <div className="pt-4 border-top mt-4 ps-4">
                                <PromptConfigSection
                                    isOrgScope={true}
                                    promptHeader={form.prompt_header || ''}
                                    onPromptHeaderChange={(value) => handleChange('prompt_header', value)}
                                    promptBody={form.prompt_body || ''}
                                    onPromptBodyChange={(value) => handleChange('prompt_body', value)}
                                    promptFooter={form.prompt_footer || ''}
                                    onPromptFooterChange={(value) => handleChange('prompt_footer', value)}
                                    shareContactInfo={form.share_contact_info ?? null}
                                    onShareContactInfoChange={(value) => handleChange('share_contact_info', value)}
                                    shareQuotes={form.share_quotes ?? null}
                                    onShareQuotesChange={(value) => handleChange('share_quotes', value)}
                                    sharePolicies={form.share_policies ?? null}
                                    onSharePoliciesChange={(value) => handleChange('share_policies', value)}
                                    shareLeads={form.share_leads ?? null}
                                    onShareLeadsChange={(value) => handleChange('share_leads', value)}
                                    shareConsentStatus={form.share_consent_status ?? null}
                                    onShareConsentStatusChange={(value) => handleChange('share_consent_status', value)}
                                />
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* ═════ EMAIL SYSTEM ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-envelope fs-2 text-warning"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Email System</h4>
                    </div>
                    {/* ─── Email SMTP ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            {/* <i className="bi bi-envelope fs-4 text-warning"></i> */}
                            <h5 className="fw-bold text-gray-800 mb-0">Email SMTP</h5>
                            <Form.Check
                                type="switch"
                                id="email_enabled"
                                checked={form.email_enabled || false}
                                onChange={(e) => handleChange('email_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.email_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.email_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.email_enabled && (
                            <>
                                <div className="row g-1 mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">SMTP Host</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="smtp.gmail.com"
                                                value={form.email_host || ''}
                                                onChange={(e) => handleChange('email_host', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Port</Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="587"
                                                value={form.email_port || 587}
                                                onChange={(e) => handleChange('email_port', parseInt(e.target.value) || 587)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                id="email_use_tls"
                                                label="TLS"
                                                checked={form.email_use_tls !== false}
                                                onChange={(e) => handleChange('email_use_tls', e.target.checked)}
                                                className="mb-0 fs-7"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">User</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="user@example.com"
                                                value={form.email_host_user || ''}
                                                onChange={(e) => handleChange('email_host_user', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row g-1 mt-1 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                autoComplete="off"
                                                placeholder="••••••••"
                                                value={form.email_host_password || ''}
                                                onChange={(e) => handleChange('email_host_password', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">From</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="noti@example.com"
                                                value={form.email_from || ''}
                                                onChange={(e) => handleChange('email_from', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Reply-To</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="support@example.com"
                                                value={form.email_reply_to || ''}
                                                onChange={(e) => handleChange('email_reply_to', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* ═════ INTEGRATIONS ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-link-45deg fs-2 text-success"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Integrations</h4>
                    </div>
                    {/* ─── HealthSherpa Medicare ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-heart-pulse fs-4 text-danger"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">HealthSherpa Medicare</h5>
                            <Form.Check
                                type="switch"
                                id="healthsherpa_enabled"
                                checked={form.healthsherpa_enabled || false}
                                onChange={(e) => handleChange('healthsherpa_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.healthsherpa_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.healthsherpa_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.healthsherpa_enabled && (
                            <div className="row g-1 mt-2 align-items-end">
                                <div className="col-md-4">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Agent Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="agent@example.com"
                                            value={form.healthsherpa_email || ''}
                                            onChange={(e) => handleChange('healthsherpa_email', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">API Key</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Key"
                                            value={form.healthsherpa_api_key || ''}
                                            onChange={(e) => handleChange('healthsherpa_api_key', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-5">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Webhook Secret</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Secret"
                                            value={form.healthsherpa_webhook_secret || ''}
                                            onChange={(e) => handleChange('healthsherpa_webhook_secret', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
            </>
            )}

            {/* Channel Form Modal */}
            {decodedOrgId && (
                <ChannelFormModal
                    isOpen={channelModalOpen}
                    onClose={() => {
                        setChannelModalOpen(false)
                        setEditingChannel(null)
                    }}
                    onSave={handleSaveChannel}
                    saving={channelsSaving}
                    initialData={editingChannel}
                    groupMembers={[]} // Empty for org-level channels
                />
            )}
        </div>
    )
}
